import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  Shield, TrendingUp, Target, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, XCircle,
  Brain, Scale, Activity, BookOpen, BarChart3
} from 'lucide-react';

// FAQ Data for Schema
const faqData = [
  {
    question: "Risk yönetimi ne demek?",
    answer: "Olası zararı sınırlama yaklaşımıdır."
  },
  {
    question: "Stop Loss risk yönetimi midir?",
    answer: "Evet. Temel bileşenlerden biridir."
  },
  {
    question: "Risk yönetimi zararı engeller mi?",
    answer: "Hayır. Amaç sınırlandırmaktır."
  },
  {
    question: "Yeni başlayanlar neden zorlanır?",
    answer: "Piyasa dinamik ve volatil olabilir."
  },
  {
    question: "Risk yönetimi neden önemlidir?",
    answer: "Kontrol ve sürdürülebilirlik sağlar."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Yeni Başlayanlar İçin Risk Yönetimi – Kripto İşlemlerde Temel Mantık",
    "description": "Kripto işlemlerde risk yönetimi nedir? Stop Loss, pozisyon kontrolü ve sermaye korunumu mantığını basitçe öğrenin.",
    "author": {
      "@type": "Organization",
      "name": "Borsamex"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Borsamex",
      "logo": {
        "@type": "ImageObject",
        "url": "https://borsamex.com/logo512.png"
      }
    },
    "datePublished": "2025-12-24",
    "dateModified": "2025-12-24",
    "mainEntityOfPage": "https://borsamex.com/rehber/yeni-baslayanlar-icin-risk-yonetimi"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const RiskYonetimiPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Yeni Başlayanlar İçin Risk Yönetimi – Kripto İşlemlerde Temel Mantık | Borsamex"
        description="Kripto işlemlerde risk yönetimi nedir? Stop Loss, pozisyon kontrolü ve sermaye korunumu mantığını basitçe öğrenin. Yeni başlayanlar için rehber."
        keywords="risk yönetimi nedir, kripto risk yönetimi, stop loss neden önemli, pozisyon kontrolü, sermaye koruma, kripto yeni başlayan"
        canonical="https://borsamex.com/rehber/yeni-baslayanlar-icin-risk-yonetimi"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Risk Yönetimi</span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-red-500/10 text-red-400 border-red-500/20">İleri</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>7 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Yeni Başlayanlar İçin Risk Yönetimi
            </h1>
            
            {/* Featured Snippet Target */}
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">Risk yönetimi</strong>, olası zararı tamamen engellemek değil, 
                <strong className="text-red-400"> kontrol altında tutma</strong> yaklaşımıdır.
              </p>
              <p className="text-slate-400 mt-4">
                Kripto piyasasında amaç sadece fırsat aramak değil, belirsizliğe karşı planlı hareket etmektir.
              </p>
            </div>
          </motion.header>

          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="prose prose-invert prose-slate max-w-none"
          >
            
            {/* Risk Yönetimi Nedir */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-red-400" />
                </div>
                Risk Yönetimi Nedir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Risk yönetimi, bir işlem senaryosunda karşılaşılabilecek olumsuz sonuçları sınırlama yaklaşımıdır.
              </p>
              
              <p className="text-slate-300 mb-4">Bu kavramın odağı:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Zararın kontrolü</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Senaryo disiplini</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Sermaye korunumu</span>
                </li>
              </ul>
              
              <Card className="border-slate-700/50 bg-slate-900/30">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    Amaç kusursuz tahmin yapmak değildir.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Kripto Piyasasında Risk */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Activity className="h-4 w-4 text-amber-400" />
                </div>
                Kripto Piyasasında Risk Neden Önemlidir?
              </h2>
              
              <p className="text-slate-300 mb-4">Kripto piyasası doğası gereği:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Volatilite içerir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Ani fiyat hareketlerine açıktır</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Belirsizlik barındırır</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                Bu yapı, plansız işlemleri daha riskli hale getirir.
              </p>
            </section>

            {/* Stop Loss Mantığı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-red-400" />
                </div>
                Stop Loss Mantığı
              </h2>
              
              <p className="text-slate-300 mb-4">
                Stop Loss, risk yönetiminin temel bileşenidir.
              </p>
              
              <p className="text-slate-300 mb-4">Bu seviye:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Senaryonun geçersiz noktasıdır</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Zarar garantisi değildir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Kontrol mekanizmasıdır</span>
                </li>
              </ul>
              
              <Card className="border-red-500/30 bg-red-950/20">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    Stop'un amacı kayıp değil, <strong className="text-white">sınırdır</strong>.
                  </p>
                </CardContent>
              </Card>

              <div className="flex gap-4 mt-6">
                <Link to="/rehber/tp-ve-stop-loss-nedir">
                  <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    TP ve Stop Loss Rehberi →
                  </Button>
                </Link>
              </div>
            </section>

            {/* Inline AI Sinyal Kutusu */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">BTC-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Giriş: $67,200 | Stop: $65,800 | Risk/Ödül: 1:2.5
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Stop Loss ile risk kontrollü senaryo</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Borsamex'te her sinyal otomatik stop seviyesi içerir. Risk yönetimi sistemin temelindedir.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Risk Kontrollü Sinyalleri Gör <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* Pozisyon Kontrolü */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Scale className="h-4 w-4 text-purple-400" />
                </div>
                Pozisyon Kontrolü
              </h2>
              
              <p className="text-slate-300 mb-4">
                Risk yönetimi yalnızca fiyat seviyesi değildir.
              </p>
              
              <p className="text-slate-300 mb-4">İşlem planlarında:</p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>Pozisyon büyüklüğü</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>Senaryo dengesi</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>Risk dağılımı</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                gibi faktörler de önemlidir.
              </p>
            </section>

            {/* Yaygın Yanılgı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                </div>
                Yaygın Yanılgı
              </h2>
              
              <p className="text-slate-300 mb-4">Yeni kullanıcılar genellikle:</p>
              
              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Sadece hedefe odaklanır</span>
                </div>
                <div className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Risk sınırını göz ardı eder</span>
                </div>
                <div className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Stop'u gereksiz görür</span>
                </div>
              </div>
              
              <p className="text-slate-400">
                Oysa piyasa tahmin edilemez olabilir.
              </p>
            </section>

            {/* Profesyonel Yaklaşım */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-green-400" />
                </div>
                Profesyonel Yaklaşım
              </h2>
              
              <p className="text-slate-300 mb-4">Disiplinli işlem mantığında:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                  <span>Olası kazanç ve olası risk birlikte değerlendirilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                  <span>Senaryo mantığı korunur</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                  <span>Kontrol mekanizmaları tanımlanır</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                Bu yaklaşım kesinlik iddiası içermez.
              </p>
            </section>

            {/* Risk ve Psikoloji */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Brain className="h-4 w-4 text-blue-400" />
                </div>
                Risk ve Psikoloji
              </h2>
              
              <p className="text-slate-300 mb-4">
                Kripto işlemlerde kararlar yalnızca teknik değildir.
              </p>
              
              <p className="text-slate-300 mb-4">Duygusal tepkiler:</p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <AlertTriangle className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Acele girişler</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <AlertTriangle className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Plansız çıkışlar</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <AlertTriangle className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Aşırı risk alma davranışı</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                gibi sonuçlar doğurabilir.
              </p>
            </section>

            {/* Borsamex Sistem Yaklaşımı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-brand-green" />
                </div>
                Borsamex Sistem Yaklaşımı
              </h2>
              
              <p className="text-slate-300 mb-4">Borsamex sinyallerinde:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Stop Loss seviyesi zorunlu bileşendir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk yapısı önceliklendirilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>TP seviyeleri tek başına sunulmaz</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>AI yalnızca açıklama üretir</span>
                </li>
              </ul>

              <div className="flex gap-4">
                <Link to="/ai-kripto-sinyalleri">
                  <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    AI Sinyalleri Hakkında →
                  </Button>
                </Link>
              </div>
            </section>

            {/* İlgili Konular - Internal Links */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-blue-400" />
                </div>
                İlgili Konular
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/rehber/kripto-sinyali-nedir" className="group">
                  <Card className="border-purple-500/20 bg-slate-900/50 hover:border-purple-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Zap className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Kripto Sinyali Nedir?</p>
                          <p className="text-xs text-slate-500">Temel kavramları öğren</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/tp-ve-stop-loss-nedir" className="group">
                  <Card className="border-green-500/20 bg-slate-900/50 hover:border-green-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Target className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-green-400 transition-colors">TP ve Stop Loss Nedir?</p>
                          <p className="text-xs text-slate-500">Hedef ve risk seviyeleri</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/kripto-sinyali-nasil-okunur" className="group">
                  <Card className="border-amber-500/20 bg-slate-900/50 hover:border-amber-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="h-5 w-5 text-amber-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-amber-400 transition-colors">Sinyal Nasıl Okunur?</p>
                          <p className="text-xs text-slate-500">Pratik sinyal okuma rehberi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/performans" className="group">
                  <Card className="border-cyan-500/20 bg-slate-900/50 hover:border-cyan-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Activity className="h-5 w-5 text-cyan-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-cyan-400 transition-colors">Performans Verileri</p>
                          <p className="text-xs text-slate-500">Şeffaf sonuçları gör</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            {/* Önemli Not */}
            <section className="mb-12">
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-amber-400 mb-2">Önemli Not</h3>
                      <p className="text-slate-300">
                        Hiçbir sistem risk içermez hale getiremez.
                        Tüm işlemler belirsizlik barındırır.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <ShareBox 
              title="Yeni Baslayanlar Icin Risk Yonetimi Rehberi"
              url="/rehber/yeni-baslayanlar-icin-risk-yonetimi"
              hashtags="#kripto #riskyonetimi #trading #bitcoin"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/kripto-sinyali-nasil-okunur" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Kripto Sinyali Nasıl Okunur?
            </Link>
            <Link to="/rehber" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Tüm Rehberler
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-red-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Risk Yönetimi Mantığını Öğrendin
            </h3>
            <p className="text-slate-400 mb-6">
              Şimdi canlı sinyalleri takip et ve her sinyalde risk yapısını gör.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default RiskYonetimiPage;
